import { useState } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  Button,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import {
  buttonRed,
  buttonRedText,
  dark,
  newBorder,
  primary,
  primaryAlt,
  textPrimary,
  textSecondary,
  white,
} from "../../../utils/colors";
import { useTranslation } from "react-i18next";
import { requestPasswordResetAction } from "../../../actions/authActions";
import { useDispatch, useSelector } from "react-redux";
import { AUTH_RESET_PASSWORD_RESET } from "../../../constants/authConstants";

const RequestResetPassword = ({ setResetPasswordOpen }) => {
  const { t } = useTranslation();
  const toast = useToast();

  // STATE
  const [email, setEmail] = useState("");

  const storedLanguage = localStorage.getItem("language");
  const initialLanguage = storedLanguage || "en"; // Default to 'en'
  const [currentLanguage, setCurrentLanguage] = useState(initialLanguage);

  // VALIDATION
  const isEmailValidated = !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(
    email
  );

  // Email validation: Standard email format
  const isEmailEmpty = email === "";
  const isEmailInvalid =
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email) && !isEmailEmpty;

  // TOOLTIP VALIDATION MESSAGES
  const tooltipMessages = [];

  // Email error messages
  if (isEmailEmpty) {
    tooltipMessages.push(t("formError.emailRequired"));
  } else if (isEmailInvalid) {
    tooltipMessages.push(t("formError.emailInvalid"));
  }

  // REDUX
  const dispatch = useDispatch();

  const authRequestResetPassword = useSelector(
    (state) => state.authRequestResetPassword
  );
  const { loading, error, success } = authRequestResetPassword;

  // HANDLERS
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleBack = () => {
    setResetPasswordOpen(false);
    setEmail("");
    dispatch({ type: AUTH_RESET_PASSWORD_RESET });
  };

  const handleResetPassword = async () => {
    try {
      await dispatch(requestPasswordResetAction(email, currentLanguage));
      // ✅ Show toast on success
      toast({
        title: t("toasts.resetPasswordSuccess"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      const message = err?.response?.data?.message || "Unknown error";
      console.error("Reset password error:", message);

      if (message === "Tenant was not found by this email.") {
        toast({
          title: t("toasts.resetPasswordErrorNoAccount"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else if (
        message ===
        "This account uses Google login. Please sign in with Google."
      ) {
        toast({
          title: t("toasts.resetPasswordErrorGoogle"),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("toasts.resetPasswordError"),
          //   description: message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      dispatch({ type: AUTH_RESET_PASSWORD_RESET }); // optional: clear reducer state after toast
    }
  };

  // console.log("success", success);
  // console.log("error", error);
  // console.log("loading", loading);

  return (
    <>
      {!success ? (
        <Flex
          w="100%"
          maxW={["100%", "420px"]}
          p={["4rem 1.5rem", "initial"]}
          direction="column"
        >
          <form
            style={{ width: "100%" }}
            onSubmit={(e) => {
              e.preventDefault();
              handleResetPassword();
            }}
          >
            <Box>
              <Heading
                fontSize="1.875rem"
                fontWeight="700"
                color={textPrimary}
                //   mb={6}
                m="0 0 0.5rem"
              >
                {t("register.resetPasswordToken")}
              </Heading>
              <Text
                fontSize="1rem"
                fontWeight="500"
                color={textSecondary}
                //   mb={6}
                lineHeight="1.5rem"
                textAlign="left"
                maxW={["90%", "initial"]}
              >
                {t("register.resetPasswordTokenText")}
              </Text>
            </Box>

            <Box m="3rem 0 0">
              <FormControl>
                <FormLabel htmlFor="email">{t("form.email")}</FormLabel>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  bg={["rgba(255, 255, 255, 0.6)", "none", "none"]}
                  onChange={handleEmailChange}
                  required
                  size="lg"
                  h="3.5rem"
                  fontSize="1rem"
                  fontWeight="500"
                  border={[`1px solid ${newBorder}`]}
                  borderRadius={["10rem", "10rem", "1rem"]}
                  placeholder={t("placeholder.email")}
                  mb={4}
                  _focus={{
                    border: `1px solid ${primary}`,
                    boxShadow: "none !important",
                  }}
                />
              </FormControl>

              <Box m="1rem 0 0">
                <Tooltip
                  bg={buttonRed}
                  color={buttonRedText}
                  padding="0.5rem 1rem"
                  borderRadius="1rem"
                  placement="top"
                  label={
                    tooltipMessages.length > 0 ? (
                      <Flex direction="column" justify="center" align="center">
                        {tooltipMessages.map((message, index) => (
                          <Text
                            key={index}
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                            textAlign="center"
                          >
                            {message}
                          </Text>
                        ))}
                      </Flex>
                    ) : null
                  }
                  isDisabled={tooltipMessages.length === 0} // Disable tooltip if no errors
                >
                  <Button
                    type="submit"
                    h="3.5rem"
                    fontSize="md"
                    bg={dark}
                    color={white}
                    w="100%"
                    borderRadius="10rem"
                    isDisabled={tooltipMessages.length > 0} // Disable if any errors exist
                    _hover={{
                      bg: primaryAlt,
                      color: white,
                    }}
                  >
                    {t("register.resetPasswordButtonLabel")}
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </form>
        </Flex>
      ) : (
        <>
          <Flex w="100%" maxW="420px" direction="column">
            <Box>
              <Heading
                fontSize="1.875rem"
                fontWeight="700"
                color={textPrimary}
                //   mb={6}
                m="0 0 0.5rem"
              >
                {t("register.resetPasswordSentSuccess")}
              </Heading>
              <Text
                fontSize="1rem"
                fontWeight="500"
                color={textSecondary}
                //   mb={6}
                lineHeight="1.5rem"
                textAlign="left"
              >
                {email && (
                  <Text as="span" color={primaryAlt} mr={1.5}>
                    {email}
                  </Text>
                )}
                {t("register.resetPasswordSentSuccessText")}
              </Text>

              <Button
                onClick={handleBack}
                m="2.5rem 0 0"
                mb={4}
                h="3.5rem"
                borderRadius="10rem"
                bg={dark}
                color={white}
                // border={`1px solid ${dark}`}
                textAlign="center"
                w="100%"
                _hover={{
                  bg: primaryAlt,
                  color: white,
                  border: `1px solid ${primaryAlt}`,
                }}
              >
                {t("common.login")}
              </Button>
            </Box>
          </Flex>
        </>
      )}
    </>
  );
};

export default RequestResetPassword;
